import { createContext, useContext, useState, ReactNode } from "react";

export type Language = "en" | "sv" | "fi" | "ru" | "de" | "cs" | "fr";

interface TournamentNames {
  olympics: string;
  wcSenior: string;
  wcU20: string;
  wcU18: string;
  wcU18Women: string;
  womensWc: string;
  channelOneCup: string;
  finnishGames: string;
  deutschlandCup: string;
  fortunaHockeyGames: string;
  beijerGames: string;
  spenglerCup: string;
  chl: string;
  // Leagues
  nhl: string;
  ahl: string;
  echl: string;
  khl: string;
  shl: string;
  liiga: string;
  extraliga: string;
  nationalLeague: string;
  del: string;
  // Women's Leagues
  pwhl: string;
  sdhl: string;
  naistenLiiga: string;
}

interface RulebookRules {
  periods: string;
  periodsDesc: string;
  intermissions: string;
  intermissionsDesc: string;
  players: string;
  playersDesc: string;
  scoring: string;
  scoringDesc: string;
  penalties: string;
  penaltiesDesc: string;
  offside: string;
  offsideDesc: string;
  overtime: string;
  overtimeDesc: string;
}

interface BettingTranslations {
  title: string;
  subtitle: string;
  disclaimer: string;
  responsibleGambling: string;
  liveOdds: string;
}

interface PlayerTranslations {
  title: string;
  subtitle: string;
  lineup: string;
  stats: string;
  draft: string;
  goalieStats: string;
  advancedStats: string;
  forwards: string;
  defense: string;
  goalies: string;
  player: string;
  pos: string;
  gp: string;
  goals: string;
  assists: string;
  points: string;
  pim: string;
  overall: string;
  year: string;
  round: string;
  draftedBy: string;
  wins: string;
  losses: string;
  otl: string;
  gaa: string;
  svPct: string;
  shutouts: string;
  cf: string;
  ca: string;
  cfPct: string;
  ff: string;
  fa: string;
  ffPct: string;
  xGF: string;
  xGA: string;
  xGPct: string;
  toi: string;
}

interface Translations {
  // Navbar
  nav: {
    scores: string;
    schedule: string;
    standings: string;
    teams: string;
    stats: string;
    international: string;
    signIn: string;
    subscribe: string;
  };
  // Hero
  hero: {
    title: string;
    subtitle: string;
    watchLive: string;
    fullSchedule: string;
  };
  // Scores & Results
  games: {
    title: string;
    subtitle: string;
    viewSchedule: string;
    live: string;
    today: string;
    broadcast: string;
    puckDrop: string;
    watchOn: string;
    watchLive: string;
    setReminder: string;
    odds: string;
    placeBet: string;
  };
  // Stats
  stats: {
    title: string;
    subtitle: string;
    gamesPlayed: string;
    avgGoals: string;
    powerPlay: string;
    savePercentage: string;
    fromLastSeason: string;
  };
  // Standings
  standings: {
    title: string;
    subtitle: string;
    viewFull: string;
    rank: string;
    team: string;
    gamesPlayed: string;
    wins: string;
    losses: string;
    otLosses: string;
    points: string;
    streak: string;
  };
  // Tournaments
  tournaments: {
    title: string;
    subtitle: string;
    internationalTitle: string;
    leaguesTitle: string;
    upcoming: string;
    live: string;
    completed: string;
    teams: string;
    viewDetails: string;
    viewAll: string;
    names: TournamentNames;
  };
  // Rulebook
  rulebook: {
    title: string;
    subtitle: string;
    fullRulebook: string;
    rules: RulebookRules;
  };
  // Betting
  betting: BettingTranslations;
  // Players
  players: PlayerTranslations;
  // Footer
  footer: {
    tagline: string;
    quickLinks: string;
    resources: string;
    company: string;
    home: string;
    schedule: string;
    standings: string;
    teams: string;
    api: string;
    support: string;
    contact: string;
    about: string;
    careers: string;
    press: string;
    copyright: string;
  };
  // Hero extras
  heroExtra: {
    season: string;
    alertsLine: string;
  };
  // Home Teaser
  homeTeaser: {
    personalization: string;
    title: string;
    subtitle: string;
    nationTracking: string;
    teamAlerts: string;
    playerStats: string;
    seeDemo: string;
    signUpFree: string;
    joinMessage: string;
    draftWatch: string;
    draftWatchDesc: string;
  };
  // Your Teams
  yourTeams: {
    signInTitle: string;
    signInDesc: string;
    signIn: string;
    followTitle: string;
    followDesc: string;
    chooseTeams: string;
    upgradePro: string;
    upgradeNow: string;
    title: string;
    alertsOn: string;
    alertsOff: string;
    edit: string;
    upgradeToEdit: string;
    noGames: string;
    checkBack: string;
    viewAll: string;
  };
  // Your Players
  yourPlayers: {
    signInTitle: string;
    signInDesc: string;
    signIn: string;
    followTitle: string;
    followDesc: string;
    choosePlayers: string;
    upgradePro: string;
    upgradeNow: string;
    title: string;
    alertsOn: string;
    alertsOff: string;
    edit: string;
    upgradeToEdit: string;
    viewFullStats: string;
    premiumStats: string;
    signInStats: string;
    viewAll: string;
  };
  // Big Events
  bigEvents: {
    markCalendar: string;
    title: string;
    subtitle: string;
    getAlerts: string;
    whereToWatch: string;
    scheduleSoon: string;
    moreInfoSoon: string;
    nextGame: string;
  };
  // Navbar extras
  navExtra: {
    pricing: string;
    myPage: string;
    myTeams: string;
    myPlayers: string;
    account: string;
    signOut: string;
    managePlan: string;
  };
  // Mobile Sidebar
  sidebar: {
    quickNav: string;
    home: string;
    liveResults: string;
    leagues: string;
    bettingOdds: string;
    statistics: string;
    playerStats: string;
    playoffBracket: string;
    international: string;
    scheduleWatch: string;
    olympics2026: string;
    leagueStandings: string;
    hockeyRules: string;
    tapToNavigate: string;
  };
  // Prospects Section
  prospects: {
    sectionTitle: string;
    sectionSubtitle: string;
    lastUpdated: string;
    disclaimer: string;
    tabProspects: string;
    tabRookies: string;
    tabNews: string;
    draftProspectsTitle: string;
    viewFullRankings: string;
    position: string;
    league: string;
    rank: string;
    name: string;
    pos: string;
    team: string;
    ht: string;
    wt: string;
    scoutingNote: string;
    media: string;
    noProspects: string;
    rookiesTitle: string;
    rookiesSubtitle: string;
    noRookies: string;
    rookieTeaser: string;
    rookieTeaserDesc: string;
    liveNews: string;
    newsSource: string;
    scoutingResources: string;
    latestArticles: string;
    loadingNews: string;
    noNews: string;
    neverMiss: string;
    neverMissDesc: string;
    subscribeAlerts: string;
  };
}

const translations: Record<Language, Translations> = {
  en: {
    nav: {
      scores: "Live & Results",
      schedule: "Schedule & Watch",
      standings: "Standings",
      teams: "Teams",
      stats: "Stats",
      international: "International",
      signIn: "Sign In",
      subscribe: "Subscribe",
    },
    hero: {
      title: "Feel The Ice",
      subtitle: "Live scores, stats, and schedules for every game. Never miss a moment of the action.",
      watchLive: "Live Scores",
      fullSchedule: "Schedule & Watch",
    },
    games: {
      title: "Live & Results",
      subtitle: "Live scores & final results",
      viewSchedule: "Schedule & Watch →",
      live: "Live",
      today: "Today",
      broadcast: "Broadcast",
      puckDrop: "Puck Drop",
      watchOn: "Watch on",
      watchLive: "Watch Live →",
      setReminder: "Set Reminder →",
      odds: "Betting Odds",
      placeBet: "Place Bet",
    },
    stats: {
      title: "Season Stats",
      subtitle: "Key numbers from the current season",
      gamesPlayed: "Games Played",
      avgGoals: "Avg Goals/Game",
      powerPlay: "Power Play %",
      savePercentage: "Save %",
      fromLastSeason: "from last season",
    },
    standings: {
      title: "League Standings",
      subtitle: "Current playoff picture",
      viewFull: "View Full Standings →",
      rank: "Rank",
      team: "Team",
      gamesPlayed: "GP",
      wins: "W",
      losses: "L",
      otLosses: "OTL",
      points: "PTS",
      streak: "Streak",
    },
    tournaments: {
      title: "Tournaments & Leagues",
      subtitle: "World championships, Olympics, and professional leagues",
      internationalTitle: "International Tournaments",
      leaguesTitle: "Professional Leagues",
      upcoming: "Upcoming",
      live: "Live",
      completed: "Completed",
      teams: "teams",
      viewDetails: "View Details",
      viewAll: "View All Tournaments →",
      names: {
        olympics: "Olympic Games",
        wcSenior: "World Championship",
        wcU20: "World Juniors (U20)",
        wcU18: "World Juniors (U18)",
        wcU18Women: "U18 Women's World Championship",
        womensWc: "Women's World Championship",
        channelOneCup: "Channel One Cup",
        finnishGames: "Finnish Ice Hockey Games",
        deutschlandCup: "Deutschland Cup",
        fortunaHockeyGames: "Fortuna Hockey Games",
        beijerGames: "Beijer Hockey Games",
        spenglerCup: "Spengler Cup",
        chl: "Champions Hockey League",
        nhl: "NHL",
        ahl: "AHL (American Hockey League)",
        echl: "ECHL (East Coast Hockey League)",
        khl: "KHL",
        shl: "SHL (Swedish Hockey League)",
        liiga: "Liiga",
        extraliga: "Czech Extraliga",
        nationalLeague: "National League",
        del: "DEL (Deutsche Eishockey Liga)",
        pwhl: "PWHL (Professional Women's Hockey League)",
        sdhl: "SDHL (Swedish Women's Hockey League)",
        naistenLiiga: "Naisten Liiga (Finnish Women's League)",
      },
    },
    rulebook: {
      title: "Rulebook",
      subtitle: "Key Hockey Rules",
      fullRulebook: "Full IIHF Rulebook →",
      rules: {
        periods: "Game Periods",
        periodsDesc: "A standard game consists of three 20-minute periods of play.",
        intermissions: "Intermissions",
        intermissionsDesc: "Senior/professional hockey has 15-minute breaks between periods (17-18 min for TV). Junior leagues use 20-minute intermissions for ice resurfacing and player recovery.",
        players: "Players on Ice",
        playersDesc: "Each team has 6 players on the ice: 1 goaltender and 5 skaters (typically 3 forwards and 2 defensemen).",
        scoring: "Scoring",
        scoringDesc: "A goal is scored when the puck completely crosses the goal line between the posts and under the crossbar.",
        penalties: "Penalties",
        penaltiesDesc: "Minor penalties result in 2 minutes, major penalties 5 minutes. The penalized player serves time in the penalty box.",
        offside: "Offside",
        offsideDesc: "A player cannot enter the attacking zone before the puck. The puck must completely cross the blue line first.",
        overtime: "Overtime",
        overtimeDesc: "If tied after regulation, games go to overtime (5 min 3-on-3) then shootout in regular season.",
      },
    },
    betting: {
      title: "Betting Odds",
      subtitle: "Compare odds from top bookmakers",
      disclaimer: "Gamble responsibly",
      responsibleGambling: "Gambling can be addictive. Please play responsibly. 18+ only. If you have a gambling problem, please seek help.",
      liveOdds: "Live Odds",
    },
    players: {
      title: "Player Center",
      subtitle: "Lineups, statistics, and draft information",
      lineup: "Lineup",
      stats: "Stats",
      draft: "Draft",
      goalieStats: "Goalies",
      advancedStats: "Advanced",
      forwards: "Forwards",
      defense: "Defense",
      goalies: "Goalies",
      player: "Player",
      pos: "POS",
      gp: "GP",
      goals: "G",
      assists: "A",
      points: "PTS",
      pim: "PIM",
      overall: "OVR",
      year: "Year",
      round: "Rd",
      draftedBy: "Drafted By",
      wins: "W",
      losses: "L",
      otl: "OTL",
      gaa: "GAA",
      svPct: "SV%",
      shutouts: "SO",
      cf: "CF",
      ca: "CA",
      cfPct: "CF%",
      ff: "FF",
      fa: "FA",
      ffPct: "FF%",
      xGF: "xGF",
      xGA: "xGA",
      xGPct: "xG%",
      toi: "TOI",
    },
    footer: {
      tagline: "Your ultimate destination for hockey scores, stats, and live coverage.",
      quickLinks: "Quick Links",
      resources: "Resources",
      company: "Company",
      home: "Home",
      schedule: "Schedule & Watch",
      standings: "Standings",
      teams: "Teams",
      api: "API",
      support: "Support",
      contact: "Contact",
      about: "About",
      careers: "Careers",
      press: "Press",
      copyright: "All rights reserved.",
    },
    heroExtra: {
      season: "Season 2025-26",
      alertsLine: "Live Scores, Fixtures & Broadcast Guide – Free Alerts for Your Teams",
    },
    homeTeaser: {
      personalization: "Personalization",
      title: "Build Your Hockey Hub",
      subtitle: "Follow your favorite nation, teams, and players. Get a personalized dashboard with live scores, stats, and priority game alerts.",
      nationTracking: "Nation tracking",
      teamAlerts: "Team alerts",
      playerStats: "Player stats",
      seeDemo: "See Demo",
      signUpFree: "Sign Up Free",
      joinMessage: "Join thousands of hockey fans tracking their teams worldwide",
      draftWatch: "2026 Draft Watch",
      draftWatchDesc: "Top prospects, rookie stats, scouting videos & draft rankings",
    },
    yourTeams: {
      signInTitle: "Sign In to Track Your Teams",
      signInDesc: "Create an account to follow your favorite leagues and teams. Get personalized game updates and scores.",
      signIn: "Sign In",
      followTitle: "Follow Your Favorite Teams",
      followDesc: "Select your favorite leagues and teams to personalize your IceHub experience. Get quick access to scores, schedules, and game alerts.",
      chooseTeams: "Choose Your Teams",
      upgradePro: "Upgrade to Pro to customize your dashboard",
      upgradeNow: "Upgrade Now",
      title: "Your Teams",
      alertsOn: "Alerts On",
      alertsOff: "Alerts Off",
      edit: "Edit",
      upgradeToEdit: "Upgrade to Edit",
      noGames: "No games today for your favorite teams",
      checkBack: "Check back later or view the full schedule",
      viewAll: "View all",
    },
    yourPlayers: {
      signInTitle: "Sign In to Track Your Players",
      signInDesc: "Create an account to follow your favorite players from NHL, KHL, SHL, Liiga, and more. Get personalized updates on stats, goals, and upcoming games.",
      signIn: "Sign In",
      followTitle: "Follow Your Favorite Players",
      followDesc: "Track players from NHL, KHL, SHL, Liiga, and more. Get personalized updates on stats, goals, and upcoming games.",
      choosePlayers: "Choose Your Players",
      upgradePro: "Upgrade to Pro to customize your players",
      upgradeNow: "Upgrade Now",
      title: "Your Players",
      alertsOn: "Alerts On",
      alertsOff: "Alerts Off",
      edit: "Edit",
      upgradeToEdit: "Upgrade to Edit",
      viewFullStats: "View Full Stats",
      premiumStats: "Premium Stats",
      signInStats: "Sign in for live stats",
      viewAll: "View all",
    },
    bigEvents: {
      markCalendar: "Mark Your Calendar",
      title: "Upcoming Big Events",
      subtitle: "Never miss a game – major tournaments and playoffs coming up",
      getAlerts: "Get Event Alerts – Free!",
      whereToWatch: "Where to Watch",
      scheduleSoon: "Schedule coming soon",
      moreInfoSoon: "More info coming soon",
      nextGame: "Next Game",
    },
    navExtra: {
      pricing: "Pricing",
      myPage: "My Page",
      myTeams: "My Teams",
      myPlayers: "My Players",
      account: "Account",
      signOut: "Sign Out",
      managePlan: "Manage Plan",
    },
    sidebar: {
      quickNav: "Quick Navigation",
      home: "Home",
      liveResults: "Live & Results",
      leagues: "Leagues & Tournaments",
      bettingOdds: "Betting Odds",
      statistics: "Statistics",
      playerStats: "Player Stats",
      playoffBracket: "Playoff Bracket",
      international: "International",
      scheduleWatch: "Schedule & Watch",
      olympics2026: "Olympics 2026",
      leagueStandings: "League Standings",
      hockeyRules: "Hockey Rules",
      tapToNavigate: "Tap to navigate • Swipe to close",
    },
    prospects: {
      sectionTitle: "Draft, Rookies & News",
      sectionSubtitle: "2026 NHL Draft rankings, rookie watch & live hockey news",
      lastUpdated: "Last updated: February 28, 2026",
      disclaimer: "Prospect rankings are projections – subject to change.",
      tabProspects: "Prospects",
      tabRookies: "Watch",
      tabNews: "News Feed",
      draftProspectsTitle: "2025 NHL Draft Prospects",
      viewFullRankings: "View Full Rankings",
      position: "Position:",
      league: "League:",
      rank: "Rank",
      name: "Name",
      pos: "Pos",
      team: "Team",
      ht: "Ht",
      wt: "Wt",
      scoutingNote: "Scouting Note",
      media: "Media",
      noProspects: "No prospects match the selected filters",
      rookiesTitle: "Rookies Watch 2024-25",
      rookiesSubtitle: "Top first-year players making an impact across leagues",
      noRookies: "No rookies match the selected filter",
      rookieTeaser: "More rookie highlights coming soon",
      rookieTeaserDesc: "expanded stats, Calder Trophy tracker, and video breakdowns are on the way.",
      liveNews: "Live Hockey News",
      newsSource: "Aggregated from EliteProspects, NHL.com & DobberProspects",
      scoutingResources: "Scouting Resources",
      latestArticles: "Latest Articles",
      loadingNews: "Loading news…",
      noNews: "No news articles available right now. Check back later.",
      neverMiss: "Never miss a breaking update",
      neverMissDesc: "Pro & Elite members get instant alerts on trades, draft moves, and roster changes.",
      subscribeAlerts: "Subscribe for Alerts",
    },
  },
  sv: {
    nav: {
      scores: "Live & Resultat",
      schedule: "Schema & TV",
      standings: "Tabell",
      teams: "Lag",
      stats: "Statistik",
      international: "Internationellt",
      signIn: "Logga in",
      subscribe: "Prenumerera",
    },
    hero: {
      title: "Känn Isen",
      subtitle: "Liveresultat, statistik och spelscheman för varje match. Missa aldrig ett ögonblick.",
      watchLive: "Liveresultat",
      fullSchedule: "Schema & TV",
    },
    games: {
      title: "Live & Resultat",
      subtitle: "Liveresultat & slutresultat",
      viewSchedule: "Schema & TV →",
      live: "Live",
      today: "Idag",
      broadcast: "Sändning",
      puckDrop: "Nedsläpp",
      watchOn: "Se på",
      watchLive: "Se Live →",
      setReminder: "Sätt Påminnelse →",
      odds: "Odds",
      placeBet: "Spela",
    },
    stats: {
      title: "Säsongsstatistik",
      subtitle: "Nyckeltal från nuvarande säsong",
      gamesPlayed: "Spelade Matcher",
      avgGoals: "Snitt Mål/Match",
      powerPlay: "Powerplay %",
      savePercentage: "Räddnings %",
      fromLastSeason: "från förra säsongen",
    },
    standings: {
      title: "Tabellen",
      subtitle: "Aktuell slutspelsbild",
      viewFull: "Visa Hela Tabellen →",
      rank: "Plats",
      team: "Lag",
      gamesPlayed: "S",
      wins: "V",
      losses: "F",
      otLosses: "ÖTF",
      points: "P",
      streak: "Svit",
    },
    rulebook: {
      title: "Regelbok",
      subtitle: "Viktiga Hockeyregler",
      fullRulebook: "Hela IIHF Regelboken →",
      rules: {
        periods: "Spelperioder",
        periodsDesc: "En standardmatch består av tre 20-minutersperioder.",
        intermissions: "Pauser",
        intermissionsDesc: "Senior/professionell hockey har 15 minuters pauser (17-18 min för TV). Juniorligor använder 20 minuters pauser för isbearbetning och spelaråterhämtning.",
        players: "Spelare på Isen",
        playersDesc: "Varje lag har 6 spelare på isen: 1 målvakt och 5 utespelare (vanligtvis 3 forwards och 2 backar).",
        scoring: "Mål",
        scoringDesc: "Ett mål räknas när pucken helt passerar mållinjen mellan stolparna och under ribban.",
        penalties: "Utvisningar",
        penaltiesDesc: "Mindre utvisningar ger 2 minuter, större 5 minuter. Den utvisade spelaren sitter i utvisningsbåset.",
        offside: "Offside",
        offsideDesc: "En spelare får inte gå in i anfallszonen före pucken. Pucken måste helt passera den blå linjen först.",
        overtime: "Övertid",
        overtimeDesc: "Vid oavgjort efter ordinarie tid spelas övertid (5 min 3-mot-3) sedan straffar i grundserien.",
      },
    },
    tournaments: {
      title: "Turneringar & Ligor",
      subtitle: "VM, OS och professionella ligor",
      internationalTitle: "Internationella Turneringar",
      leaguesTitle: "Professionella Ligor",
      upcoming: "Kommande",
      live: "Live",
      completed: "Avslutad",
      teams: "lag",
      viewDetails: "Visa Detaljer",
      viewAll: "Visa Alla Turneringar →",
      names: {
        olympics: "Olympiska Spelen",
        wcSenior: "Världsmästerskapet",
        wcU20: "Junior-VM (U20)",
        wcU18: "Junior-VM (U18)",
        wcU18Women: "U18 Dam-VM",
        womensWc: "Dam-VM",
        channelOneCup: "Channel One Cup",
        finnishGames: "Finska Ishockeyspelen",
        deutschlandCup: "Deutschland Cup",
        fortunaHockeyGames: "Fortuna Hockey Games",
        beijerGames: "Beijer Hockey Games",
        spenglerCup: "Spengler Cup",
        chl: "Champions Hockey League",
        nhl: "NHL",
        ahl: "AHL (American Hockey League)",
        echl: "ECHL (East Coast Hockey League)",
        khl: "KHL",
        shl: "SHL (Svenska Hockeyligan)",
        liiga: "Liiga",
        extraliga: "Tjeckiska Extraligan",
        nationalLeague: "National League",
        del: "DEL (Deutsche Eishockey Liga)",
        pwhl: "PWHL (Professionell Damhockeyliga)",
        sdhl: "SDHL (Svenska Damhockeyligan)",
        naistenLiiga: "Naisten Liiga (Finska Damhockeyligan)",
      },
    },
    betting: {
      title: "Odds",
      subtitle: "Jämför odds från toppspelbolag",
      disclaimer: "Spela ansvarsfullt",
      responsibleGambling: "Spel kan vara beroendeframkallande. Spela ansvarsfullt. 18+ år. Om du har spelproblem, sök hjälp.",
      liveOdds: "Live Odds",
    },
    players: {
      title: "Spelarcenter",
      subtitle: "Laguppställningar, statistik och draftinformation",
      lineup: "Uppställning",
      stats: "Statistik",
      draft: "Draft",
      goalieStats: "Målvakter",
      advancedStats: "Avancerat",
      forwards: "Forwards",
      defense: "Backar",
      goalies: "Målvakter",
      player: "Spelare",
      pos: "POS",
      gp: "S",
      goals: "M",
      assists: "A",
      points: "P",
      pim: "UTM",
      overall: "TOT",
      year: "År",
      round: "Rd",
      draftedBy: "Draftad Av",
      wins: "V",
      losses: "F",
      otl: "ÖTF",
      gaa: "IM",
      svPct: "R%",
      shutouts: "NS",
      cf: "CF",
      ca: "CA",
      cfPct: "CF%",
      ff: "FF",
      fa: "FA",
      ffPct: "FF%",
      xGF: "xGF",
      xGA: "xGA",
      xGPct: "xG%",
      toi: "TOI",
    },
    footer: {
      tagline: "Din ultimata destination för hockeyresultat, statistik och livebevakning.",
      quickLinks: "Snabblänkar",
      resources: "Resurser",
      company: "Företag",
      home: "Hem",
      schedule: "Schema",
      standings: "Tabell",
      teams: "Lag",
      api: "API",
      support: "Support",
      contact: "Kontakt",
      about: "Om Oss",
      careers: "Karriär",
      press: "Press",
      copyright: "Alla rättigheter förbehållna.",
    },
    heroExtra: {
      season: "Säsong 2025-26",
      alertsLine: "Livescores, spelschema & sändningsguide – Gratis aviseringar för dina lag",
    },
    homeTeaser: {
      personalization: "Personalisering",
      title: "Bygg Ditt Hockeycenter",
      subtitle: "Följ din favoritnation, lag och spelare. Få en personlig dashboard med livescores, statistik och prioriterade matchaviseringar.",
      nationTracking: "Nationsspårning",
      teamAlerts: "Lagaviseringar",
      playerStats: "Spelarstatistik",
      seeDemo: "Se Demo",
      signUpFree: "Registrera Gratis",
      joinMessage: "Gå med tusentals hockeyfans som följer sina lag världen över",
      draftWatch: "2026 Draft Watch",
      draftWatchDesc: "Toppprospects, rookiestatistik, scoutingvideor & draftrankningar",
    },
    yourTeams: {
      signInTitle: "Logga in för att följa dina lag",
      signInDesc: "Skapa ett konto för att följa dina favoritligor och lag. Få personliga matchuppdateringar och resultat.",
      signIn: "Logga In",
      followTitle: "Följ Dina Favoritlag",
      followDesc: "Välj dina favoritligor och lag för att anpassa din IceHub-upplevelse. Få snabb åtkomst till resultat, spelscheman och matchaviseringar.",
      chooseTeams: "Välj Dina Lag",
      upgradePro: "Uppgradera till Pro för att anpassa din dashboard",
      upgradeNow: "Uppgradera Nu",
      title: "Dina Lag",
      alertsOn: "Aviseringar På",
      alertsOff: "Aviseringar Av",
      edit: "Redigera",
      upgradeToEdit: "Uppgradera för att redigera",
      noGames: "Inga matcher idag för dina favoritlag",
      checkBack: "Kom tillbaka senare eller se hela schemat",
      viewAll: "Visa alla",
    },
    yourPlayers: {
      signInTitle: "Logga in för att följa dina spelare",
      signInDesc: "Skapa ett konto för att följa dina favoritspelare från NHL, KHL, SHL, Liiga med mera. Få personliga uppdateringar om statistik, mål och kommande matcher.",
      signIn: "Logga In",
      followTitle: "Följ Dina Favoritspelare",
      followDesc: "Följ spelare från NHL, KHL, SHL, Liiga med mera. Få personliga uppdateringar om statistik, mål och kommande matcher.",
      choosePlayers: "Välj Dina Spelare",
      upgradePro: "Uppgradera till Pro för att anpassa dina spelare",
      upgradeNow: "Uppgradera Nu",
      title: "Dina Spelare",
      alertsOn: "Aviseringar På",
      alertsOff: "Aviseringar Av",
      edit: "Redigera",
      upgradeToEdit: "Uppgradera för att redigera",
      viewFullStats: "Visa Full Statistik",
      premiumStats: "Premiumstatistik",
      signInStats: "Logga in för livestatistik",
      viewAll: "Visa alla",
    },
    bigEvents: {
      markCalendar: "Markera i Kalendern",
      title: "Kommande Stora Evenemang",
      subtitle: "Missa aldrig en match – stora turneringar och slutspel framöver",
      getAlerts: "Få Eventaviseringar – Gratis!",
      whereToWatch: "Var Kan Du Se",
      scheduleSoon: "Schema kommer snart",
      moreInfoSoon: "Mer info kommer snart",
      nextGame: "Nästa Match",
    },
    navExtra: {
      pricing: "Priser",
      myPage: "Min Sida",
      myTeams: "Mina Lag",
      myPlayers: "Mina Spelare",
      account: "Konto",
      signOut: "Logga Ut",
      managePlan: "Hantera Abonnemang",
    },
    sidebar: {
      quickNav: "Snabbnavigering",
      home: "Hem",
      liveResults: "Live & Resultat",
      leagues: "Ligor & Turneringar",
      bettingOdds: "Odds",
      statistics: "Statistik",
      playerStats: "Spelarstatistik",
      playoffBracket: "Slutspelsträd",
      international: "Internationellt",
      scheduleWatch: "Schema & TV",
      olympics2026: "OS 2026",
      leagueStandings: "Tabellen",
      hockeyRules: "Hockeyregler",
      tapToNavigate: "Tryck för att navigera • Svep för att stänga",
    },
    prospects: {
      sectionTitle: "Draft, Rookies & Nyheter",
      sectionSubtitle: "2026 NHL Draft-rankningar, rookiebevakning & live hockeynyheter",
      lastUpdated: "Senast uppdaterad: 28 februari 2026",
      disclaimer: "Prospektrankningar är prognoser – kan ändras.",
      tabProspects: "Prospects",
      tabRookies: "Bevakning",
      tabNews: "Nyheter",
      draftProspectsTitle: "2025 NHL Draft Prospects",
      viewFullRankings: "Visa Hela Listan",
      position: "Position:",
      league: "Liga:",
      rank: "Rank",
      name: "Namn",
      pos: "Pos",
      team: "Lag",
      ht: "Hd",
      wt: "Vt",
      scoutingNote: "Scoutingnotering",
      media: "Media",
      noProspects: "Inga prospects matchar de valda filtren",
      rookiesTitle: "Rookies Bevakning 2024-25",
      rookiesSubtitle: "Topprookies som gör avtryck i ligor",
      noRookies: "Inga rookies matchar det valda filtret",
      rookieTeaser: "Fler rookiehöjdpunkter kommer snart",
      rookieTeaserDesc: "utökad statistik, Calder Trophy-tracker och videoanalyser är på väg.",
      liveNews: "Live Hockeynyheter",
      newsSource: "Aggregerat från EliteProspects, NHL.com & DobberProspects",
      scoutingResources: "Scoutingresurser",
      latestArticles: "Senaste Artiklarna",
      loadingNews: "Laddar nyheter…",
      noNews: "Inga nyhetsartiklar tillgängliga just nu. Kolla tillbaka senare.",
      neverMiss: "Missa aldrig en viktig uppdatering",
      neverMissDesc: "Pro- & Elite-medlemmar får omedelbara aviseringar om byten, draftändringar och rosterförändringar.",
      subscribeAlerts: "Prenumerera på Aviseringar",
    },
  },
  fi: {
    nav: {
      scores: "Live & Tulokset",
      schedule: "Aikataulu & TV",
      standings: "Sarjataulukko",
      teams: "Joukkueet",
      stats: "Tilastot",
      international: "Kansainvälinen",
      signIn: "Kirjaudu",
      subscribe: "Tilaa",
    },
    hero: {
      title: "Tunne Jää",
      subtitle: "Livetulokset, tilastot ja aikataulut jokaiseen peliin. Älä missaa hetkeäkään.",
      watchLive: "Livetulokset",
      fullSchedule: "Aikataulu & TV",
    },
    games: {
      title: "Live & Tulokset",
      subtitle: "Livetulokset & lopputulokset",
      viewSchedule: "Aikataulu & TV →",
      live: "Live",
      today: "Tänään",
      broadcast: "Lähetys",
      puckDrop: "Aloitus",
      watchOn: "Katso",
      watchLive: "Katso Live →",
      setReminder: "Aseta Muistutus →",
      odds: "Vedonlyöntikertoimet",
      placeBet: "Lyö Vetoa",
    },
    stats: {
      title: "Kauden Tilastot",
      subtitle: "Avainluvut tältä kaudelta",
      gamesPlayed: "Pelatut Pelit",
      avgGoals: "Maalit/Peli",
      powerPlay: "Ylivoima %",
      savePercentage: "Torjunta %",
      fromLastSeason: "viime kaudesta",
    },
    standings: {
      title: "Sarjataulukko",
      subtitle: "Playoff-tilanne",
      viewFull: "Näytä Koko Taulukko →",
      rank: "Sija",
      team: "Joukkue",
      gamesPlayed: "O",
      wins: "V",
      losses: "H",
      otLosses: "JH",
      points: "P",
      streak: "Putki",
    },
    rulebook: {
      title: "Sääntökirja",
      subtitle: "Tärkeät Jääkiekkosäännöt",
      fullRulebook: "Koko IIHF Sääntökirja →",
      rules: {
        periods: "Pelierät",
        periodsDesc: "Tavallinen ottelu koostuu kolmesta 20 minuutin erästä.",
        intermissions: "Erätauot",
        intermissionsDesc: "Aikuisten/ammattilaisjääkiekossa erätauot ovat 15 minuuttia (17-18 min TV-lähetyksissä). Juniorisarjoissa erätauot ovat 20 minuuttia jään kunnostusta ja pelaajien palautumista varten.",
        players: "Pelaajat Jäällä",
        playersDesc: "Jokaisella joukkueella on 6 pelaajaa jäällä: 1 maalivahti ja 5 kenttäpelaajaa (yleensä 3 hyökkääjää ja 2 puolustajaa).",
        scoring: "Maalinteko",
        scoringDesc: "Maali syntyy, kun kiekko ylittää kokonaan maaliviivan tolppien välissä ja yläputken alla.",
        penalties: "Jäähyt",
        penaltiesDesc: "Pienemmät rangaistukset 2 minuuttia, suuremmat 5 minuuttia. Rangaistuksen saanut pelaaja istuu jäähyaitiossa.",
        offside: "Paitsio",
        offsideDesc: "Pelaaja ei saa mennä hyökkäysalueelle ennen kiekkoa. Kiekon täytyy ylittää sininen viiva kokonaan ensin.",
        overtime: "Jatkoaika",
        overtimeDesc: "Tasapelin jälkeen pelataan jatkoaika (5 min 3-3) ja tarvittaessa voittolaukauskilpailu runkosarjassa.",
      },
    },
    tournaments: {
      title: "Turnaukset & Liigat",
      subtitle: "MM-kisat, olympialaiset ja ammattiliigat",
      internationalTitle: "Kansainväliset Turnaukset",
      leaguesTitle: "Ammattiliigat",
      upcoming: "Tulossa",
      live: "Live",
      completed: "Päättynyt",
      teams: "joukkuetta",
      viewDetails: "Katso Tiedot",
      viewAll: "Katso Kaikki Turnaukset →",
      names: {
        olympics: "Olympialaiset",
        wcSenior: "MM-kisat",
        wcU20: "Nuorten MM (U20)",
        wcU18: "Nuorten MM (U18)",
        wcU18Women: "U18 Naisten MM",
        womensWc: "Naisten MM-kisat",
        channelOneCup: "Channel One Cup",
        finnishGames: "Suomen jääkiekkopelit",
        deutschlandCup: "Deutschland Cup",
        fortunaHockeyGames: "Fortuna Hockey Games",
        beijerGames: "Beijer Hockey Games",
        spenglerCup: "Spengler Cup",
        chl: "Champions Hockey League",
        nhl: "NHL",
        ahl: "AHL (American Hockey League)",
        echl: "ECHL (East Coast Hockey League)",
        khl: "KHL",
        shl: "SHL (Ruotsin liiga)",
        liiga: "Liiga",
        extraliga: "Tšekin Extraliiga",
        nationalLeague: "National League",
        del: "DEL (Saksan liiga)",
        pwhl: "PWHL (Ammattilaisnaisten jääkiekkoliiga)",
        sdhl: "SDHL (Ruotsin naisten liiga)",
        naistenLiiga: "Naisten Liiga",
      },
    },
    betting: {
      title: "Vedonlyöntikertoimet",
      subtitle: "Vertaa kertoimia huippuvedonvälittäjiltä",
      disclaimer: "Pelaa vastuullisesti",
      responsibleGambling: "Uhkapeli voi aiheuttaa riippuvuutta. Pelaa vastuullisesti. Vain 18+. Jos sinulla on peliongelma, hae apua.",
      liveOdds: "Live-kertoimet",
    },
    players: {
      title: "Pelaajat",
      subtitle: "Kokoonpanot, tilastot ja varaukset",
      lineup: "Kokoonpano",
      stats: "Tilastot",
      draft: "Varaus",
      goalieStats: "Maalivahdit",
      advancedStats: "Edistyneet",
      forwards: "Hyökkääjät",
      defense: "Puolustajat",
      goalies: "Maalivahdit",
      player: "Pelaaja",
      pos: "POS",
      gp: "O",
      goals: "M",
      assists: "S",
      points: "P",
      pim: "JM",
      overall: "KOK",
      year: "Vuosi",
      round: "Krs",
      draftedBy: "Varasi",
      wins: "V",
      losses: "H",
      otl: "JH",
      gaa: "PM",
      svPct: "T%",
      shutouts: "NO",
      cf: "CF",
      ca: "CA",
      cfPct: "CF%",
      ff: "FF",
      fa: "FA",
      ffPct: "FF%",
      xGF: "xGF",
      xGA: "xGA",
      xGPct: "xG%",
      toi: "JA",
    },
    footer: {
      tagline: "Täydellinen kohde jääkiekkotuloksille, tilastoille ja livelähetyksille.",
      quickLinks: "Pikalinkit",
      resources: "Resurssit",
      company: "Yritys",
      home: "Koti",
      schedule: "Aikataulu",
      standings: "Taulukko",
      teams: "Joukkueet",
      api: "API",
      support: "Tuki",
      contact: "Yhteystiedot",
      about: "Tietoa",
      careers: "Työpaikat",
      press: "Lehdistö",
      copyright: "Kaikki oikeudet pidätetään.",
    },
    heroExtra: {
      season: "Kausi 2025-26",
      alertsLine: "Livetulokset, otteluohjelma & lähetysopas – Ilmaiset hälytykset joukkueillesi",
    },
    homeTeaser: {
      personalization: "Personointi",
      title: "Rakenna Oma Kiekkokeskus",
      subtitle: "Seuraa suosikkimaatasi, joukkueitasi ja pelaajiasi. Saat henkilökohtaisen dashboardin livetuloksilla, tilastoilla ja otteluhälytyksillä.",
      nationTracking: "Maaseuranta",
      teamAlerts: "Joukkuehälytykset",
      playerStats: "Pelaajatilastot",
      seeDemo: "Katso Demo",
      signUpFree: "Rekisteröidy Ilmaiseksi",
      joinMessage: "Liity tuhansien jääkiekkofanien joukkoon, jotka seuraavat joukkueitaan maailmanlaajuisesti",
      draftWatch: "2026 Draft Watch",
      draftWatchDesc: "Huippulupaukset, tulokastilastot, scoutingvideot & draftlistat",
    },
    yourTeams: {
      signInTitle: "Kirjaudu seurataksesi joukkueitasi",
      signInDesc: "Luo tili seurataksesi suosikkiliigojasi ja joukkueitasi. Saa henkilökohtaiset ottelupäivitykset ja tulokset.",
      signIn: "Kirjaudu",
      followTitle: "Seuraa Suosikkijoukkueitasi",
      followDesc: "Valitse suosikkiliigasi ja joukkueesi IceHub-kokemuksen personointiin. Saa nopea pääsy tuloksiin, aikatauluihin ja otteluhälytyksiin.",
      chooseTeams: "Valitse Joukkueesi",
      upgradePro: "Päivitä Pro-tiliin muokataksesi dashboardia",
      upgradeNow: "Päivitä Nyt",
      title: "Joukkueesi",
      alertsOn: "Hälytykset Päällä",
      alertsOff: "Hälytykset Pois",
      edit: "Muokkaa",
      upgradeToEdit: "Päivitä muokataksesi",
      noGames: "Ei otteluita tänään suosikkijoukkueillesi",
      checkBack: "Tarkista myöhemmin tai katso koko ohjelma",
      viewAll: "Näytä kaikki",
    },
    yourPlayers: {
      signInTitle: "Kirjaudu seurataksesi pelaajiasi",
      signInDesc: "Luo tili seurataksesi suosikkipelaajiasi NHL:stä, KHL:stä, SHL:stä, Liigasta ja muualta. Saa henkilökohtaiset päivitykset tilastoista, maaleista ja tulevista peleistä.",
      signIn: "Kirjaudu",
      followTitle: "Seuraa Suosikkipelaajiasi",
      followDesc: "Seuraa pelaajia NHL:stä, KHL:stä, SHL:stä, Liigasta ja muualta. Saa henkilökohtaiset päivitykset tilastoista, maaleista ja tulevista peleistä.",
      choosePlayers: "Valitse Pelaajasi",
      upgradePro: "Päivitä Pro-tiliin muokataksesi pelaajiasi",
      upgradeNow: "Päivitä Nyt",
      title: "Pelaajasi",
      alertsOn: "Hälytykset Päällä",
      alertsOff: "Hälytykset Pois",
      edit: "Muokkaa",
      upgradeToEdit: "Päivitä muokataksesi",
      viewFullStats: "Näytä Kaikki Tilastot",
      premiumStats: "Premium-tilastot",
      signInStats: "Kirjaudu livetilastoihin",
      viewAll: "Näytä kaikki",
    },
    bigEvents: {
      markCalendar: "Merkitse Kalenteriin",
      title: "Tulevat Suurtapahtumat",
      subtitle: "Älä missaa peliäkään – suuret turnaukset ja pudotuspelit tulossa",
      getAlerts: "Saa Tapahtumahälytykset – Ilmaiseksi!",
      whereToWatch: "Mistä Katsoa",
      scheduleSoon: "Aikataulu tulossa pian",
      moreInfoSoon: "Lisätietoja tulossa pian",
      nextGame: "Seuraava Peli",
    },
    navExtra: {
      pricing: "Hinnat",
      myPage: "Oma Sivu",
      myTeams: "Joukkueeni",
      myPlayers: "Pelaajani",
      account: "Tili",
      signOut: "Kirjaudu Ulos",
      managePlan: "Hallitse Tilausta",
    },
    sidebar: {
      quickNav: "Pikanavigointi",
      home: "Koti",
      liveResults: "Live & Tulokset",
      leagues: "Liigat & Turnaukset",
      bettingOdds: "Vedonlyöntikertoimet",
      statistics: "Tilastot",
      playerStats: "Pelaajatilastot",
      playoffBracket: "Pudotuspelikaavio",
      international: "Kansainvälinen",
      scheduleWatch: "Aikataulu & TV",
      olympics2026: "Olympialaiset 2026",
      leagueStandings: "Sarjataulukko",
      hockeyRules: "Jääkiekkosäännöt",
      tapToNavigate: "Napauta navigoidaksesi • Pyyhkäise sulkeaksesi",
    },
    prospects: {
      sectionTitle: "Draft, Tulokkaat & Uutiset",
      sectionSubtitle: "2026 NHL Draft -sijoitukset, tulokasseuranta & live-jääkiekkouutiset",
      lastUpdated: "Viimeksi päivitetty: 28. helmikuuta 2026",
      disclaimer: "Prospektisijoitukset ovat arvioita – voivat muuttua.",
      tabProspects: "Prospektit",
      tabRookies: "Seuranta",
      tabNews: "Uutiset",
      draftProspectsTitle: "2025 NHL Draft Prospektit",
      viewFullRankings: "Näytä Koko Lista",
      position: "Pelipaikka:",
      league: "Liiga:",
      rank: "Sija",
      name: "Nimi",
      pos: "Pos",
      team: "Joukkue",
      ht: "Pituus",
      wt: "Paino",
      scoutingNote: "Scouting-merkintä",
      media: "Media",
      noProspects: "Yksikään prospekti ei vastaa valittuja suodattimia",
      rookiesTitle: "Tulokkaat 2024-25",
      rookiesSubtitle: "Parhaiten pärjäävät ensimmäisen vuoden pelaajat liigoissa",
      noRookies: "Yksikään tulokas ei vastaa valittua suodatinta",
      rookieTeaser: "Lisää tulokashighlighteja tulossa pian",
      rookieTeaserDesc: "laajennetut tilastot, Calder Trophy -seuranta ja videoanalyysit tulossa.",
      liveNews: "Live-jääkiekkouutiset",
      newsSource: "Koottu EliteProspects, NHL.com & DobberProspects -sivustoilta",
      scoutingResources: "Scouting-resurssit",
      latestArticles: "Uusimmat Artikkelit",
      loadingNews: "Ladataan uutisia…",
      noNews: "Ei uutisartikkeleita saatavilla juuri nyt. Tarkista myöhemmin.",
      neverMiss: "Älä missaa yhtään tärkeää päivitystä",
      neverMissDesc: "Pro- & Elite-jäsenet saavat välittömiä hälytyksiä kaupoista, draftmuutoksista ja kokoonpanomuutoksista.",
      subscribeAlerts: "Tilaa Hälytykset",
    },
  },
  ru: {
    nav: {
      scores: "Live & Результаты",
      schedule: "Расписание & ТВ",
      standings: "Таблица",
      teams: "Команды",
      stats: "Статистика",
      international: "Международный",
      signIn: "Войти",
      subscribe: "Подписаться",
    },
    hero: {
      title: "Почувствуй Лёд",
      subtitle: "Живые результаты, статистика и расписание каждой игры. Не пропустите ни момента.",
      watchLive: "Живые Счета",
      fullSchedule: "Расписание & ТВ",
    },
    games: {
      title: "Live & Результаты",
      subtitle: "Живые результаты & итоги матчей",
      viewSchedule: "Расписание & ТВ →",
      live: "Live",
      today: "Сегодня",
      broadcast: "Трансляция",
      puckDrop: "Начало",
      watchOn: "Смотреть на",
      watchLive: "Смотреть Live →",
      setReminder: "Напоминание →",
      odds: "Коэффициенты",
      placeBet: "Сделать Ставку",
    },
    stats: {
      title: "Статистика Сезона",
      subtitle: "Ключевые показатели текущего сезона",
      gamesPlayed: "Игр Сыграно",
      avgGoals: "Голов/Игра",
      powerPlay: "Большинство %",
      savePercentage: "Сейвы %",
      fromLastSeason: "с прошлого сезона",
    },
    standings: {
      title: "Турнирная Таблица",
      subtitle: "Текущая картина плей-офф",
      viewFull: "Полная Таблица →",
      rank: "Место",
      team: "Команда",
      gamesPlayed: "И",
      wins: "В",
      losses: "П",
      otLosses: "ОТП",
      points: "О",
      streak: "Серия",
    },
    rulebook: {
      title: "Правила",
      subtitle: "Основные Правила Хоккея",
      fullRulebook: "Полный Свод Правил IIHF →",
      rules: {
        periods: "Периоды Игры",
        periodsDesc: "Стандартный матч состоит из трёх 20-минутных периодов.",
        intermissions: "Перерывы",
        intermissionsDesc: "В профессиональном хоккее перерывы между периодами — 15 минут (17-18 мин для ТВ). В юниорских лигах — 20 минут для подготовки льда и восстановления игроков.",
        players: "Игроки на Льду",
        playersDesc: "Каждая команда имеет 6 игроков на льду: 1 вратарь и 5 полевых игроков (обычно 3 нападающих и 2 защитника).",
        scoring: "Голы",
        scoringDesc: "Гол засчитывается, когда шайба полностью пересекает линию ворот между штангами и под перекладиной.",
        penalties: "Штрафы",
        penaltiesDesc: "Малые штрафы — 2 минуты, большие — 5 минут. Оштрафованный игрок отбывает время на скамейке штрафников.",
        offside: "Офсайд",
        offsideDesc: "Игрок не может войти в зону атаки до шайбы. Шайба должна полностью пересечь синюю линию первой.",
        overtime: "Овертайм",
        overtimeDesc: "При ничьей после основного времени играется овертайм (5 мин 3-на-3), затем буллиты в регулярном сезоне.",
      },
    },
    tournaments: {
      title: "Турниры и Лиги",
      subtitle: "Чемпионаты мира, Олимпиада и профессиональные лиги",
      internationalTitle: "Международные Турниры",
      leaguesTitle: "Профессиональные Лиги",
      upcoming: "Скоро",
      live: "Live",
      completed: "Завершён",
      teams: "команд",
      viewDetails: "Подробнее",
      viewAll: "Все Турниры →",
      names: {
        olympics: "Олимпийские Игры",
        wcSenior: "Чемпионат Мира",
        wcU20: "Молодёжный ЧМ (U20)",
        wcU18: "Молодёжный ЧМ (U18)",
        wcU18Women: "U18 Женский ЧМ",
        womensWc: "Женский Чемпионат Мира",
        channelOneCup: "Кубок Первого Канала",
        finnishGames: "Финские хоккейные игры",
        deutschlandCup: "Кубок Германии",
        fortunaHockeyGames: "Кубок Fortuna",
        beijerGames: "Шведские Хоккейные Игры",
        spenglerCup: "Кубок Шпенглера",
        chl: "Лига Чемпионов",
        nhl: "НХЛ",
        ahl: "АХЛ (Американская Хоккейная Лига)",
        echl: "ECHL (Хоккейная Лига Восточного Побережья)",
        khl: "КХЛ",
        shl: "SHL (Шведская лига)",
        liiga: "Лийга",
        extraliga: "Чешская Экстралига",
        nationalLeague: "Национальная Лига",
        del: "DEL (Немецкая лига)",
        pwhl: "PWHL (Профессиональная женская хоккейная лига)",
        sdhl: "SDHL (Шведская женская хоккейная лига)",
        naistenLiiga: "Найстен Лиига (Финская женская лига)",
      },
    },
    betting: {
      title: "Ставки",
      subtitle: "Сравните коэффициенты лучших букмекеров",
      disclaimer: "Играйте ответственно",
      responsibleGambling: "Азартные игры могут вызывать зависимость. Играйте ответственно. Только 18+. Если у вас проблемы с азартными играми, обратитесь за помощью.",
      liveOdds: "Live коэффициенты",
    },
    players: {
      title: "Центр Игроков",
      subtitle: "Составы, статистика и драфт",
      lineup: "Состав",
      stats: "Статистика",
      draft: "Драфт",
      goalieStats: "Вратари",
      advancedStats: "Продвинутые",
      forwards: "Нападающие",
      defense: "Защитники",
      goalies: "Вратари",
      player: "Игрок",
      pos: "ПОЗ",
      gp: "И",
      goals: "Г",
      assists: "П",
      points: "О",
      pim: "ШМ",
      overall: "№",
      year: "Год",
      round: "Рд",
      draftedBy: "Выбран",
      wins: "П",
      losses: "ПР",
      otl: "ОТ",
      gaa: "КП",
      svPct: "ОП%",
      shutouts: "СИ",
      cf: "CF",
      ca: "CA",
      cfPct: "CF%",
      ff: "FF",
      fa: "FA",
      ffPct: "FF%",
      xGF: "xGF",
      xGA: "xGA",
      xGPct: "xG%",
      toi: "ВЛ",
    },
    footer: {
      tagline: "Ваш источник хоккейных результатов, статистики и прямых трансляций.",
      quickLinks: "Быстрые Ссылки",
      resources: "Ресурсы",
      company: "Компания",
      home: "Главная",
      schedule: "Расписание",
      standings: "Таблица",
      teams: "Команды",
      api: "API",
      support: "Поддержка",
      contact: "Контакты",
      about: "О Нас",
      careers: "Карьера",
      press: "Пресса",
      copyright: "Все права защищены.",
    },
    heroExtra: {
      season: "Сезон 2025-26",
      alertsLine: "Живые результаты, расписание и гид по трансляциям – Бесплатные оповещения для ваших команд",
    },
    homeTeaser: {
      personalization: "Персонализация",
      title: "Создайте Свой Хоккейный Центр",
      subtitle: "Следите за своей любимой сборной, командами и игроками. Получите персональную панель с живыми результатами, статистикой и оповещениями о матчах.",
      nationTracking: "Отслеживание сборной",
      teamAlerts: "Оповещения о командах",
      playerStats: "Статистика игроков",
      seeDemo: "Смотреть Демо",
      signUpFree: "Регистрация Бесплатно",
      joinMessage: "Присоединяйтесь к тысячам хоккейных фанатов, следящих за своими командами по всему миру",
      draftWatch: "Драфт 2026",
      draftWatchDesc: "Топ-проспекты, статистика новичков, скаутинговые видео и рейтинги драфта",
    },
    yourTeams: {
      signInTitle: "Войдите, чтобы отслеживать свои команды",
      signInDesc: "Создайте аккаунт для отслеживания ваших любимых лиг и команд. Получайте персональные обновления матчей и результаты.",
      signIn: "Войти",
      followTitle: "Следите за Любимыми Командами",
      followDesc: "Выберите ваши любимые лиги и команды для персонализации IceHub. Получите быстрый доступ к результатам, расписанию и оповещениям.",
      chooseTeams: "Выберите Команды",
      upgradePro: "Перейдите на Pro для настройки панели",
      upgradeNow: "Обновить Сейчас",
      title: "Ваши Команды",
      alertsOn: "Оповещения Вкл",
      alertsOff: "Оповещения Выкл",
      edit: "Изменить",
      upgradeToEdit: "Обновить для редактирования",
      noGames: "Сегодня нет игр для ваших избранных команд",
      checkBack: "Проверьте позже или посмотрите полное расписание",
      viewAll: "Показать все",
    },
    yourPlayers: {
      signInTitle: "Войдите, чтобы отслеживать игроков",
      signInDesc: "Создайте аккаунт для отслеживания ваших любимых игроков из НХЛ, КХЛ, SHL, Лийги и других лиг. Получайте обновления статистики, голов и предстоящих игр.",
      signIn: "Войти",
      followTitle: "Следите за Любимыми Игроками",
      followDesc: "Отслеживайте игроков из НХЛ, КХЛ, SHL, Лийги и других лиг. Получайте обновления статистики, голов и предстоящих игр.",
      choosePlayers: "Выберите Игроков",
      upgradePro: "Перейдите на Pro для настройки игроков",
      upgradeNow: "Обновить Сейчас",
      title: "Ваши Игроки",
      alertsOn: "Оповещения Вкл",
      alertsOff: "Оповещения Выкл",
      edit: "Изменить",
      upgradeToEdit: "Обновить для редактирования",
      viewFullStats: "Полная Статистика",
      premiumStats: "Премиум Статистика",
      signInStats: "Войдите для live статистики",
      viewAll: "Показать все",
    },
    bigEvents: {
      markCalendar: "Отметьте в Календаре",
      title: "Предстоящие Крупные События",
      subtitle: "Не пропустите ни одной игры – крупные турниры и плей-офф впереди",
      getAlerts: "Получить Оповещения – Бесплатно!",
      whereToWatch: "Где Смотреть",
      scheduleSoon: "Расписание скоро появится",
      moreInfoSoon: "Подробности скоро появятся",
      nextGame: "Следующая Игра",
    },
    navExtra: {
      pricing: "Цены",
      myPage: "Моя Страница",
      myTeams: "Мои Команды",
      myPlayers: "Мои Игроки",
      account: "Аккаунт",
      signOut: "Выйти",
      managePlan: "Управлять Подпиской",
    },
    sidebar: {
      quickNav: "Быстрая Навигация",
      home: "Главная",
      liveResults: "Live & Результаты",
      leagues: "Лиги & Турниры",
      bettingOdds: "Ставки",
      statistics: "Статистика",
      playerStats: "Статистика Игроков",
      playoffBracket: "Сетка Плей-офф",
      international: "Международный",
      scheduleWatch: "Расписание & ТВ",
      olympics2026: "Олимпиада 2026",
      leagueStandings: "Турнирная Таблица",
      hockeyRules: "Правила Хоккея",
      tapToNavigate: "Нажмите для навигации • Проведите для закрытия",
    },
    prospects: {
      sectionTitle: "Драфт, Новички & Новости",
      sectionSubtitle: "Рейтинги драфта НХЛ 2026, обзор новичков & хоккейные новости",
      lastUpdated: "Обновлено: 28 февраля 2026",
      disclaimer: "Рейтинги проспектов являются прогнозами – могут измениться.",
      tabProspects: "Проспекты",
      tabRookies: "Обзор",
      tabNews: "Новости",
      draftProspectsTitle: "Проспекты Драфта НХЛ 2025",
      viewFullRankings: "Полный Рейтинг",
      position: "Позиция:",
      league: "Лига:",
      rank: "Место",
      name: "Имя",
      pos: "Поз",
      team: "Команда",
      ht: "Рост",
      wt: "Вес",
      scoutingNote: "Скаутинг",
      media: "Медиа",
      noProspects: "Нет проспектов, соответствующих фильтрам",
      rookiesTitle: "Обзор Новичков 2024-25",
      rookiesSubtitle: "Лучшие первогодки, делающие вклад в лигах",
      noRookies: "Нет новичков, соответствующих фильтру",
      rookieTeaser: "Больше обзоров новичков скоро",
      rookieTeaserDesc: "расширенная статистика, трекер Колдер Трофи и видеоразборы на подходе.",
      liveNews: "Хоккейные Новости",
      newsSource: "Собрано с EliteProspects, NHL.com & DobberProspects",
      scoutingResources: "Скаутинговые Ресурсы",
      latestArticles: "Последние Статьи",
      loadingNews: "Загрузка новостей…",
      noNews: "Нет новостей. Проверьте позже.",
      neverMiss: "Не пропустите важные обновления",
      neverMissDesc: "Pro & Elite участники получают мгновенные оповещения о сделках, драфте и изменениях составов.",
      subscribeAlerts: "Подписаться на Оповещения",
    },
  },
  de: {
    nav: {
      scores: "Live & Ergebnisse",
      schedule: "Spielplan & TV",
      standings: "Tabelle",
      teams: "Teams",
      stats: "Statistiken",
      international: "International",
      signIn: "Anmelden",
      subscribe: "Abonnieren",
    },
    hero: {
      title: "Spüre das Eis",
      subtitle: "Live-Ergebnisse, Statistiken und Spielpläne für jedes Spiel. Verpassen Sie keinen Moment.",
      watchLive: "Live-Ergebnisse",
      fullSchedule: "Spielplan & TV",
    },
    games: {
      title: "Live & Ergebnisse",
      subtitle: "Live-Ergebnisse & Endergebnisse",
      viewSchedule: "Spielplan & TV →",
      live: "Live",
      today: "Heute",
      broadcast: "Übertragung",
      puckDrop: "Spielbeginn",
      watchOn: "Ansehen auf",
      watchLive: "Live Ansehen →",
      setReminder: "Erinnerung →",
      odds: "Wettquoten",
      placeBet: "Wetten",
    },
    stats: {
      title: "Saison Statistiken",
      subtitle: "Wichtige Zahlen der aktuellen Saison",
      gamesPlayed: "Gespielte Spiele",
      avgGoals: "Tore/Spiel",
      powerPlay: "Powerplay %",
      savePercentage: "Fangquote %",
      fromLastSeason: "zur letzten Saison",
    },
    standings: {
      title: "Tabelle",
      subtitle: "Aktuelle Playoff-Situation",
      viewFull: "Volle Tabelle →",
      rank: "Platz",
      team: "Team",
      gamesPlayed: "SP",
      wins: "S",
      losses: "N",
      otLosses: "OTN",
      points: "P",
      streak: "Serie",
    },
    rulebook: {
      title: "Regelwerk",
      subtitle: "Wichtige Eishockeyregeln",
      fullRulebook: "Vollständiges IIHF Regelwerk →",
      rules: {
        periods: "Spielperioden",
        periodsDesc: "Ein Standardspiel besteht aus drei 20-Minuten-Perioden.",
        intermissions: "Drittelpausen",
        intermissionsDesc: "Im Senioren-/Profieishockey dauern die Pausen 15 Minuten (17-18 Min für TV). Juniorligen haben 20-minütige Pausen für Eisaufbereitung und Spielererholung.",
        players: "Spieler auf dem Eis",
        playersDesc: "Jedes Team hat 6 Spieler auf dem Eis: 1 Torwart und 5 Feldspieler (typischerweise 3 Stürmer und 2 Verteidiger).",
        scoring: "Tore",
        scoringDesc: "Ein Tor wird erzielt, wenn der Puck die Torlinie zwischen den Pfosten und unter der Querstange vollständig überquert.",
        penalties: "Strafen",
        penaltiesDesc: "Kleine Strafen 2 Minuten, große Strafen 5 Minuten. Der bestrafte Spieler sitzt auf der Strafbank.",
        offside: "Abseits",
        offsideDesc: "Ein Spieler darf die Angriffszone nicht vor dem Puck betreten. Der Puck muss zuerst die blaue Linie vollständig überqueren.",
        overtime: "Verlängerung",
        overtimeDesc: "Bei Unentschieden nach regulärer Spielzeit folgt Verlängerung (5 Min 3-gegen-3), dann Shootout in der Regular Season.",
      },
    },
    tournaments: {
      title: "Turniere & Ligen",
      subtitle: "Weltmeisterschaften, Olympia und Profiligen",
      internationalTitle: "Internationale Turniere",
      leaguesTitle: "Profiligen",
      upcoming: "Demnächst",
      live: "Live",
      completed: "Beendet",
      teams: "Teams",
      viewDetails: "Details",
      viewAll: "Alle Turniere →",
      names: {
        olympics: "Olympische Spiele",
        wcSenior: "Weltmeisterschaft",
        wcU20: "Junioren-WM (U20)",
        wcU18: "Junioren-WM (U18)",
        wcU18Women: "U18 Frauen-WM",
        womensWc: "Frauen-Weltmeisterschaft",
        channelOneCup: "Channel One Cup",
        finnishGames: "Finnish Ice Hockey Games",
        deutschlandCup: "Deutschland Cup",
        fortunaHockeyGames: "Fortuna Hockey Games",
        beijerGames: "Beijer Hockey Games",
        spenglerCup: "Spengler Cup",
        chl: "Champions Hockey League",
        nhl: "NHL",
        ahl: "AHL (American Hockey League)",
        echl: "ECHL (East Coast Hockey League)",
        khl: "KHL",
        shl: "SHL (Schwedische Liga)",
        liiga: "Liiga",
        extraliga: "Tschechische Extraliga",
        nationalLeague: "National League",
        del: "DEL (Deutsche Eishockey Liga)",
        pwhl: "PWHL (Professionelle Damen-Eishockeyliga)",
        sdhl: "SDHL (Schwedische Damen-Eishockeyliga)",
        naistenLiiga: "Naisten Liiga (Finnische Damen-Liga)",
      },
    },
    betting: {
      title: "Wettquoten",
      subtitle: "Vergleichen Sie Quoten von Top-Buchmachern",
      disclaimer: "Verantwortungsvoll spielen",
      responsibleGambling: "Glücksspiel kann süchtig machen. Bitte spielen Sie verantwortungsvoll. Nur 18+. Bei Spielproblemen suchen Sie bitte Hilfe.",
      liveOdds: "Live Quoten",
    },
    players: {
      title: "Spieler-Center",
      subtitle: "Aufstellungen, Statistiken und Draft-Informationen",
      lineup: "Aufstellung",
      stats: "Statistiken",
      draft: "Draft",
      goalieStats: "Torhüter",
      advancedStats: "Erweitert",
      forwards: "Stürmer",
      defense: "Verteidiger",
      goalies: "Torhüter",
      player: "Spieler",
      pos: "POS",
      gp: "SP",
      goals: "T",
      assists: "V",
      points: "P",
      pim: "SM",
      overall: "GES",
      year: "Jahr",
      round: "Rd",
      draftedBy: "Gedraftet Von",
      wins: "S",
      losses: "N",
      otl: "OTN",
      gaa: "GT",
      svPct: "SV%",
      shutouts: "SO",
      cf: "CF",
      ca: "CA",
      cfPct: "CF%",
      ff: "FF",
      fa: "FA",
      ffPct: "FF%",
      xGF: "xGF",
      xGA: "xGA",
      xGPct: "xG%",
      toi: "EZ",
    },
    footer: {
      tagline: "Ihr ultimatives Ziel für Eishockey-Ergebnisse, Statistiken und Live-Berichterstattung.",
      quickLinks: "Schnelllinks",
      resources: "Ressourcen",
      company: "Unternehmen",
      home: "Startseite",
      schedule: "Spielplan",
      standings: "Tabelle",
      teams: "Teams",
      api: "API",
      support: "Support",
      contact: "Kontakt",
      about: "Über Uns",
      careers: "Karriere",
      press: "Presse",
      copyright: "Alle Rechte vorbehalten.",
    },
    heroExtra: {
      season: "Saison 2025-26",
      alertsLine: "Live-Ergebnisse, Spielplan & Übertragungsguide – Kostenlose Benachrichtigungen für Ihre Teams",
    },
    homeTeaser: {
      personalization: "Personalisierung",
      title: "Baue Dein Eishockey-Center",
      subtitle: "Folge deiner Lieblingsnation, deinen Teams und Spielern. Erhalte ein personalisiertes Dashboard mit Live-Ergebnissen, Statistiken und Spielbenachrichtigungen.",
      nationTracking: "Nationsverfolgung",
      teamAlerts: "Team-Benachrichtigungen",
      playerStats: "Spielerstatistiken",
      seeDemo: "Demo Ansehen",
      signUpFree: "Kostenlos Registrieren",
      joinMessage: "Schließe dich Tausenden von Eishockey-Fans an, die ihre Teams weltweit verfolgen",
      draftWatch: "2026 Draft Watch",
      draftWatchDesc: "Top-Talente, Rookie-Statistiken, Scouting-Videos & Draft-Rankings",
    },
    yourTeams: {
      signInTitle: "Anmelden, um deine Teams zu verfolgen",
      signInDesc: "Erstelle ein Konto, um deine Lieblingsligen und Teams zu verfolgen. Erhalte personalisierte Spielupdates und Ergebnisse.",
      signIn: "Anmelden",
      followTitle: "Folge Deinen Lieblingsteams",
      followDesc: "Wähle deine Lieblingsligen und Teams, um dein IceHub-Erlebnis zu personalisieren. Schneller Zugriff auf Ergebnisse, Spielpläne und Benachrichtigungen.",
      chooseTeams: "Wähle Deine Teams",
      upgradePro: "Upgrade auf Pro, um dein Dashboard anzupassen",
      upgradeNow: "Jetzt Upgraden",
      title: "Deine Teams",
      alertsOn: "Benachrichtigungen An",
      alertsOff: "Benachrichtigungen Aus",
      edit: "Bearbeiten",
      upgradeToEdit: "Upgrade zum Bearbeiten",
      noGames: "Heute keine Spiele für deine Lieblingsteams",
      checkBack: "Schaue später nochmal vorbei oder sieh dir den vollen Spielplan an",
      viewAll: "Alle anzeigen",
    },
    yourPlayers: {
      signInTitle: "Anmelden, um deine Spieler zu verfolgen",
      signInDesc: "Erstelle ein Konto, um deine Lieblingsspieler aus NHL, KHL, SHL, Liiga und mehr zu verfolgen. Erhalte Updates zu Statistiken, Toren und kommenden Spielen.",
      signIn: "Anmelden",
      followTitle: "Folge Deinen Lieblingsspielern",
      followDesc: "Verfolge Spieler aus NHL, KHL, SHL, Liiga und mehr. Erhalte Updates zu Statistiken, Toren und kommenden Spielen.",
      choosePlayers: "Wähle Deine Spieler",
      upgradePro: "Upgrade auf Pro, um deine Spieler anzupassen",
      upgradeNow: "Jetzt Upgraden",
      title: "Deine Spieler",
      alertsOn: "Benachrichtigungen An",
      alertsOff: "Benachrichtigungen Aus",
      edit: "Bearbeiten",
      upgradeToEdit: "Upgrade zum Bearbeiten",
      viewFullStats: "Volle Statistiken Anzeigen",
      premiumStats: "Premium-Statistiken",
      signInStats: "Anmelden für Live-Statistiken",
      viewAll: "Alle anzeigen",
    },
    bigEvents: {
      markCalendar: "Im Kalender Vormerken",
      title: "Kommende Große Events",
      subtitle: "Verpasse kein Spiel – große Turniere und Playoffs stehen bevor",
      getAlerts: "Event-Benachrichtigungen – Kostenlos!",
      whereToWatch: "Wo Zu Sehen",
      scheduleSoon: "Spielplan kommt bald",
      moreInfoSoon: "Weitere Infos folgen",
      nextGame: "Nächstes Spiel",
    },
    navExtra: {
      pricing: "Preise",
      myPage: "Meine Seite",
      myTeams: "Meine Teams",
      myPlayers: "Meine Spieler",
      account: "Konto",
      signOut: "Abmelden",
      managePlan: "Abo Verwalten",
    },
    sidebar: {
      quickNav: "Schnellnavigation",
      home: "Startseite",
      liveResults: "Live & Ergebnisse",
      leagues: "Ligen & Turniere",
      bettingOdds: "Wettquoten",
      statistics: "Statistiken",
      playerStats: "Spielerstatistiken",
      playoffBracket: "Playoff-Baum",
      international: "International",
      scheduleWatch: "Spielplan & TV",
      olympics2026: "Olympia 2026",
      leagueStandings: "Tabelle",
      hockeyRules: "Eishockeyregeln",
      tapToNavigate: "Tippen zum Navigieren • Wischen zum Schließen",
    },
    prospects: {
      sectionTitle: "Draft, Rookies & Nachrichten",
      sectionSubtitle: "2026 NHL Draft-Rankings, Rookie-Watch & Live-Eishockeynachrichten",
      lastUpdated: "Zuletzt aktualisiert: 28. Februar 2026",
      disclaimer: "Prospect-Rankings sind Prognosen – können sich ändern.",
      tabProspects: "Prospects",
      tabRookies: "Beobachtung",
      tabNews: "Nachrichten",
      draftProspectsTitle: "2025 NHL Draft Prospects",
      viewFullRankings: "Vollständige Rankings",
      position: "Position:",
      league: "Liga:",
      rank: "Rang",
      name: "Name",
      pos: "Pos",
      team: "Team",
      ht: "Gr",
      wt: "Gew",
      scoutingNote: "Scouting-Notiz",
      media: "Medien",
      noProspects: "Keine Prospects entsprechen den ausgewählten Filtern",
      rookiesTitle: "Rookies Watch 2024-25",
      rookiesSubtitle: "Top-Neulinge mit Einfluss in den Ligen",
      noRookies: "Keine Rookies entsprechen dem Filter",
      rookieTeaser: "Weitere Rookie-Highlights folgen",
      rookieTeaserDesc: "erweiterte Statistiken, Calder Trophy Tracker und Videoanalysen kommen bald.",
      liveNews: "Live Eishockeynachrichten",
      newsSource: "Zusammengestellt von EliteProspects, NHL.com & DobberProspects",
      scoutingResources: "Scouting-Ressourcen",
      latestArticles: "Neueste Artikel",
      loadingNews: "Nachrichten werden geladen…",
      noNews: "Derzeit keine Nachrichtenartikel verfügbar. Schauen Sie später wieder vorbei.",
      neverMiss: "Verpassen Sie keine wichtige Neuigkeit",
      neverMissDesc: "Pro- & Elite-Mitglieder erhalten sofortige Benachrichtigungen über Trades, Draft-Änderungen und Kaderänderungen.",
      subscribeAlerts: "Benachrichtigungen Abonnieren",
    },
  },
  cs: {
    nav: {
      scores: "Live & Výsledky",
      schedule: "Rozpis & TV",
      standings: "Tabulka",
      teams: "Týmy",
      stats: "Statistiky",
      international: "Mezinárodní",
      signIn: "Přihlásit",
      subscribe: "Odebírat",
    },
    hero: {
      title: "Pocit Ledu",
      subtitle: "Živé výsledky, statistiky a rozpisy všech zápasů. Nikdy nezmeškejte žádný moment.",
      watchLive: "Živé Výsledky",
      fullSchedule: "Rozpis & TV",
    },
    games: {
      title: "Live & Výsledky",
      subtitle: "Živé výsledky & konečné skóre",
      viewSchedule: "Rozpis & TV →",
      live: "Živě",
      today: "Dnes",
      broadcast: "Vysílání",
      puckDrop: "Začátek",
      watchOn: "Sledovat na",
      watchLive: "Sledovat Živě →",
      setReminder: "Nastavit Připomínku →",
      odds: "Sázkové Kurzy",
      placeBet: "Vsadit",
    },
    stats: {
      title: "Statistiky Sezóny",
      subtitle: "Klíčová čísla aktuální sezóny",
      gamesPlayed: "Odehrané Zápasy",
      avgGoals: "Góly/Zápas",
      powerPlay: "Přesilovka %",
      savePercentage: "Úspěšnost %",
      fromLastSeason: "oproti minulé sezóně",
    },
    standings: {
      title: "Tabulka",
      subtitle: "Aktuální situace v playoff",
      viewFull: "Zobrazit Celou Tabulku →",
      rank: "Pořadí",
      team: "Tým",
      gamesPlayed: "Z",
      wins: "V",
      losses: "P",
      otLosses: "PP",
      points: "B",
      streak: "Série",
    },
    rulebook: {
      title: "Pravidla",
      subtitle: "Klíčová Hokejová Pravidla",
      fullRulebook: "Kompletní Pravidla IIHF →",
      rules: {
        periods: "Třetiny",
        periodsDesc: "Standardní zápas se skládá ze tří 20minutových třetin.",
        intermissions: "Přestávky",
        intermissionsDesc: "V seniorském/profesionálním hokeji jsou přestávky 15 minut (17-18 min pro TV). Juniorské ligy mají 20minutové přestávky pro úpravu ledu a regeneraci hráčů.",
        players: "Hráči na Ledě",
        playersDesc: "Každý tým má na ledě 6 hráčů: 1 brankáře a 5 hráčů v poli (obvykle 3 útočníky a 2 obránce).",
        scoring: "Góly",
        scoringDesc: "Gól je uznán, když puk zcela přejde brankovou čáru mezi tyčemi a pod břevnem.",
        penalties: "Tresty",
        penaltiesDesc: "Menší tresty 2 minuty, větší 5 minut. Potrestaný hráč sedí na trestné lavici.",
        offside: "Ofsajd",
        offsideDesc: "Hráč nemůže vstoupit do útočného pásma před pukem. Puk musí nejdříve zcela přejet modrou čáru.",
        overtime: "Prodloužení",
        overtimeDesc: "Při remíze po základní hrací době následuje prodloužení (5 min 3 na 3), poté nájezdy v základní části.",
      },
    },
    tournaments: {
      title: "Turnaje & Ligy",
      subtitle: "Mistrovství světa, olympiáda a profesionální ligy",
      internationalTitle: "Mezinárodní Turnaje",
      leaguesTitle: "Profesionální Ligy",
      upcoming: "Nadcházející",
      live: "Živě",
      completed: "Ukončeno",
      teams: "týmů",
      viewDetails: "Zobrazit Detaily",
      viewAll: "Zobrazit Všechny Turnaje →",
      names: {
        olympics: "Olympijské Hry",
        wcSenior: "Mistrovství Světa",
        wcU20: "Juniorské MS (U20)",
        wcU18: "Juniorské MS (U18)",
        wcU18Women: "U18 MS Žen",
        womensWc: "Mistrovství Světa Žen",
        channelOneCup: "Channel One Cup",
        finnishGames: "Finnish Ice Hockey Games",
        deutschlandCup: "Deutschland Cup",
        fortunaHockeyGames: "Fortuna Hockey Games",
        beijerGames: "Beijer Hockey Games",
        spenglerCup: "Spengler Cup",
        chl: "Champions Hockey League",
        nhl: "NHL",
        ahl: "AHL (Americká Hokejová Liga)",
        echl: "ECHL (East Coast Hockey League)",
        khl: "KHL",
        shl: "SHL (Švédská liga)",
        liiga: "Liiga",
        extraliga: "Extraliga",
        nationalLeague: "National League",
        del: "DEL (Německá liga)",
        pwhl: "PWHL (Profesionální ženská hokejová liga)",
        sdhl: "SDHL (Švédská ženská hokejová liga)",
        naistenLiiga: "Naisten Liiga (Finská ženská liga)",
      },
    },
    betting: {
      title: "Sázkové Kurzy",
      subtitle: "Porovnejte kurzy od nejlepších sázkových kanceláří",
      disclaimer: "Hrajte zodpovědně",
      responsibleGambling: "Hazardní hry mohou být návykové. Hrajte prosím zodpovědně. Pouze 18+. Pokud máte problém s hazardem, vyhledejte pomoc.",
      liveOdds: "Live kurzy",
    },
    players: {
      title: "Centrum Hráčů",
      subtitle: "Sestavy, statistiky a draft",
      lineup: "Sestava",
      stats: "Statistiky",
      draft: "Draft",
      goalieStats: "Brankáři",
      advancedStats: "Pokročilé",
      forwards: "Útočníci",
      defense: "Obránci",
      goalies: "Brankáři",
      player: "Hráč",
      pos: "POZ",
      gp: "Z",
      goals: "G",
      assists: "A",
      points: "B",
      pim: "TM",
      overall: "CEL",
      year: "Rok",
      round: "Kolo",
      draftedBy: "Draftován",
      wins: "V",
      losses: "P",
      otl: "PP",
      gaa: "BP",
      svPct: "Ú%",
      shutouts: "NI",
      cf: "CF",
      ca: "CA",
      cfPct: "CF%",
      ff: "FF",
      fa: "FA",
      ffPct: "FF%",
      xGF: "xGF",
      xGA: "xGA",
      xGPct: "xG%",
      toi: "ČL",
    },
    footer: {
      tagline: "Váš nejlepší zdroj hokejových výsledků, statistik a živých přenosů.",
      quickLinks: "Rychlé Odkazy",
      resources: "Zdroje",
      company: "Společnost",
      home: "Domů",
      schedule: "Rozpis",
      standings: "Tabulka",
      teams: "Týmy",
      api: "API",
      support: "Podpora",
      contact: "Kontakt",
      about: "O Nás",
      careers: "Kariéra",
      press: "Tisk",
      copyright: "Všechna práva vyhrazena.",
    },
    heroExtra: {
      season: "Sezóna 2025-26",
      alertsLine: "Živé výsledky, rozpis & průvodce přenosy – Bezplatná upozornění pro vaše týmy",
    },
    homeTeaser: {
      personalization: "Personalizace",
      title: "Vytvořte Si Své Hokejové Centrum",
      subtitle: "Sledujte svůj oblíbený národ, týmy a hráče. Získejte personalizovaný dashboard s živými výsledky, statistikami a upozorněními na zápasy.",
      nationTracking: "Sledování národa",
      teamAlerts: "Upozornění na týmy",
      playerStats: "Statistiky hráčů",
      seeDemo: "Zobrazit Demo",
      signUpFree: "Registrace Zdarma",
      joinMessage: "Připojte se k tisícům hokejových fanoušků sledujících své týmy po celém světě",
      draftWatch: "Draft 2026",
      draftWatchDesc: "Top prospekty, statistiky nováčků, scoutingová videa & drafty",
    },
    yourTeams: {
      signInTitle: "Přihlaste se pro sledování svých týmů",
      signInDesc: "Vytvořte si účet pro sledování vašich oblíbených lig a týmů. Získejte personalizované aktualizace zápasů a výsledky.",
      signIn: "Přihlásit",
      followTitle: "Sledujte Své Oblíbené Týmy",
      followDesc: "Vyberte si oblíbené ligy a týmy pro personalizaci vašeho IceHub zážitku. Rychlý přístup k výsledkům, rozpisům a upozorněním.",
      chooseTeams: "Vyberte Své Týmy",
      upgradePro: "Upgradujte na Pro pro přizpůsobení dashboardu",
      upgradeNow: "Upgradovat Nyní",
      title: "Vaše Týmy",
      alertsOn: "Upozornění Zapnuto",
      alertsOff: "Upozornění Vypnuto",
      edit: "Upravit",
      upgradeToEdit: "Upgradovat pro úpravy",
      noGames: "Dnes žádné zápasy pro vaše oblíbené týmy",
      checkBack: "Zkuste to později nebo si prohlédněte celý rozpis",
      viewAll: "Zobrazit vše",
    },
    yourPlayers: {
      signInTitle: "Přihlaste se pro sledování svých hráčů",
      signInDesc: "Vytvořte si účet pro sledování vašich oblíbených hráčů z NHL, KHL, SHL, Liiga a dalších. Získejte aktualizace statistik, gólů a nadcházejících zápasů.",
      signIn: "Přihlásit",
      followTitle: "Sledujte Své Oblíbené Hráče",
      followDesc: "Sledujte hráče z NHL, KHL, SHL, Liiga a dalších. Získejte aktualizace statistik, gólů a nadcházejících zápasů.",
      choosePlayers: "Vyberte Své Hráče",
      upgradePro: "Upgradujte na Pro pro přizpůsobení hráčů",
      upgradeNow: "Upgradovat Nyní",
      title: "Vaši Hráči",
      alertsOn: "Upozornění Zapnuto",
      alertsOff: "Upozornění Vypnuto",
      edit: "Upravit",
      upgradeToEdit: "Upgradovat pro úpravy",
      viewFullStats: "Zobrazit Veškeré Statistiky",
      premiumStats: "Premium Statistiky",
      signInStats: "Přihlaste se pro živé statistiky",
      viewAll: "Zobrazit vše",
    },
    bigEvents: {
      markCalendar: "Poznamenejte Si Do Kalendáře",
      title: "Nadcházející Velké Události",
      subtitle: "Nepropásněte žádný zápas – velké turnaje a playoff se blíží",
      getAlerts: "Získat Upozornění – Zdarma!",
      whereToWatch: "Kde Sledovat",
      scheduleSoon: "Rozpis bude brzy k dispozici",
      moreInfoSoon: "Více informací již brzy",
      nextGame: "Další Zápas",
    },
    navExtra: {
      pricing: "Ceník",
      myPage: "Moje Stránka",
      myTeams: "Moje Týmy",
      myPlayers: "Moji Hráči",
      account: "Účet",
      signOut: "Odhlásit",
      managePlan: "Spravovat Předplatné",
    },
    sidebar: {
      quickNav: "Rychlá Navigace",
      home: "Domů",
      liveResults: "Live & Výsledky",
      leagues: "Ligy & Turnaje",
      bettingOdds: "Sázkové Kurzy",
      statistics: "Statistiky",
      playerStats: "Statistiky Hráčů",
      playoffBracket: "Pavouk Playoff",
      international: "Mezinárodní",
      scheduleWatch: "Rozpis & TV",
      olympics2026: "Olympiáda 2026",
      leagueStandings: "Tabulka",
      hockeyRules: "Hokejová Pravidla",
      tapToNavigate: "Klepněte pro navigaci • Přejeďte pro zavření",
    },
    prospects: {
      sectionTitle: "Draft, Nováčci & Zprávy",
      sectionSubtitle: "Žebříčky draftu NHL 2026, sledování nováčků & živé hokejové zprávy",
      lastUpdated: "Naposledy aktualizováno: 28. února 2026",
      disclaimer: "Žebříčky prospektů jsou projekce – mohou se změnit.",
      tabProspects: "Prospekty",
      tabRookies: "Sledování",
      tabNews: "Zprávy",
      draftProspectsTitle: "Prospekty Draftu NHL 2025",
      viewFullRankings: "Zobrazit Celý Žebříček",
      position: "Pozice:",
      league: "Liga:",
      rank: "Pořadí",
      name: "Jméno",
      pos: "Poz",
      team: "Tým",
      ht: "Výška",
      wt: "Váha",
      scoutingNote: "Scouting Poznámka",
      media: "Média",
      noProspects: "Žádní prospekti neodpovídají zvoleným filtrům",
      rookiesTitle: "Nováčci 2024-25",
      rookiesSubtitle: "Nejlepší hráči prvního roku s dopadem v ligách",
      noRookies: "Žádní nováčci neodpovídají zvolenému filtru",
      rookieTeaser: "Další highlighty nováčků již brzy",
      rookieTeaserDesc: "rozšířené statistiky, tracker Calder Trophy a videorozbory jsou na cestě.",
      liveNews: "Živé Hokejové Zprávy",
      newsSource: "Agregováno z EliteProspects, NHL.com & DobberProspects",
      scoutingResources: "Scoutingové Zdroje",
      latestArticles: "Nejnovější Články",
      loadingNews: "Načítání zpráv…",
      noNews: "Momentálně nejsou k dispozici žádné zprávy. Zkuste to později.",
      neverMiss: "Nepropásněte žádnou důležitou aktualizaci",
      neverMissDesc: "Pro & Elite členové dostávají okamžitá upozornění na výměny, draftové změny a změny v sestavách.",
      subscribeAlerts: "Odebírat Upozornění",
    },
  },
  fr: {
    nav: {
      scores: "Live & Résultats",
      schedule: "Calendrier & TV",
      standings: "Classement",
      teams: "Équipes",
      stats: "Statistiques",
      international: "International",
      signIn: "Connexion",
      subscribe: "S'abonner",
    },
    hero: {
      title: "Vivez La Glace",
      subtitle: "Scores en direct, statistiques et calendriers pour chaque match. Ne manquez aucun moment de l'action.",
      watchLive: "Scores En Direct",
      fullSchedule: "Calendrier & TV",
    },
    games: {
      title: "Live & Résultats",
      subtitle: "Scores en direct & résultats finaux",
      viewSchedule: "Calendrier & TV →",
      live: "En Direct",
      today: "Aujourd'hui",
      broadcast: "Diffusion",
      puckDrop: "Mise Au Jeu",
      watchOn: "Regarder sur",
      watchLive: "Regarder En Direct →",
      setReminder: "Définir Un Rappel →",
      odds: "Cotes",
      placeBet: "Parier",
    },
    stats: {
      title: "Statistiques De Saison",
      subtitle: "Chiffres clés de la saison en cours",
      gamesPlayed: "Matchs Joués",
      avgGoals: "Buts/Match",
      powerPlay: "Avantage Num. %",
      savePercentage: "% Arrêts",
      fromLastSeason: "par rapport à la saison dernière",
    },
    standings: {
      title: "Classement",
      subtitle: "Situation actuelle des séries",
      viewFull: "Voir Le Classement Complet →",
      rank: "Rang",
      team: "Équipe",
      gamesPlayed: "MJ",
      wins: "V",
      losses: "D",
      otLosses: "DP",
      points: "PTS",
      streak: "Série",
    },
    rulebook: {
      title: "Règlement",
      subtitle: "Règles Clés Du Hockey",
      fullRulebook: "Règlement Complet IIHF →",
      rules: {
        periods: "Périodes De Jeu",
        periodsDesc: "Un match standard se compose de trois périodes de 20 minutes.",
        intermissions: "Entractes",
        intermissionsDesc: "Le hockey senior/professionnel a des pauses de 15 minutes entre les périodes (17-18 min pour la TV). Les ligues juniors utilisent des entractes de 20 minutes pour le resurfaçage de la glace et la récupération des joueurs.",
        players: "Joueurs Sur La Glace",
        playersDesc: "Chaque équipe a 6 joueurs sur la glace : 1 gardien et 5 patineurs (généralement 3 attaquants et 2 défenseurs).",
        scoring: "Marquage",
        scoringDesc: "Un but est marqué lorsque la rondelle franchit complètement la ligne de but entre les poteaux et sous la barre transversale.",
        penalties: "Pénalités",
        penaltiesDesc: "Les pénalités mineures valent 2 minutes, les majeures 5 minutes. Le joueur pénalisé purge sa peine au banc des pénalités.",
        offside: "Hors-Jeu",
        offsideDesc: "Un joueur ne peut pas entrer dans la zone offensive avant la rondelle. La rondelle doit d'abord franchir complètement la ligne bleue.",
        overtime: "Prolongation",
        overtimeDesc: "En cas d'égalité après le temps réglementaire, le match passe en prolongation (5 min 3 contre 3) puis en tirs de barrage en saison régulière.",
      },
    },
    tournaments: {
      title: "Tournois Et Ligues",
      subtitle: "Championnats du monde, Jeux olympiques et ligues professionnelles",
      internationalTitle: "Tournois Internationaux",
      leaguesTitle: "Ligues Professionnelles",
      upcoming: "À Venir",
      live: "En Direct",
      completed: "Terminé",
      teams: "équipes",
      viewDetails: "Voir Les Détails",
      viewAll: "Voir Tous Les Tournois →",
      names: {
        olympics: "Jeux Olympiques",
        wcSenior: "Championnat Du Monde",
        wcU20: "Championnat Du Monde Junior (U20)",
        wcU18: "Championnat Du Monde Junior (U18)",
        wcU18Women: "Championnat Du Monde Féminin U18",
        womensWc: "Championnat Du Monde Féminin",
        channelOneCup: "Coupe Channel One",
        finnishGames: "Jeux De Hockey Finlandais",
        deutschlandCup: "Coupe d'Allemagne",
        fortunaHockeyGames: "Fortuna Hockey Games",
        beijerGames: "Beijer Hockey Games",
        spenglerCup: "Coupe Spengler",
        chl: "Ligue Des Champions De Hockey",
        nhl: "LNH",
        ahl: "LAH (Ligue Américaine De Hockey)",
        echl: "ECHL (East Coast Hockey League)",
        khl: "KHL",
        shl: "SHL (Ligue Suédoise)",
        liiga: "Liiga",
        extraliga: "Extraliga Tchèque",
        nationalLeague: "Ligue Nationale",
        del: "DEL (Ligue Allemande)",
        pwhl: "LPHF (Ligue Professionnelle De Hockey Féminin)",
        sdhl: "SDHL (Ligue Féminine Suédoise)",
        naistenLiiga: "Naisten Liiga (Ligue Féminine Finlandaise)",
      },
    },
    betting: {
      title: "Cotes De Paris",
      subtitle: "Comparez les cotes des meilleurs bookmakers",
      disclaimer: "Jouez de manière responsable",
      responsibleGambling: "Les jeux d'argent peuvent créer une dépendance. Jouez de manière responsable. 18+ uniquement. Si vous avez un problème de jeu, veuillez demander de l'aide.",
      liveOdds: "Cotes En Direct",
    },
    players: {
      title: "Centre Des Joueurs",
      subtitle: "Alignements, statistiques et informations sur le repêchage",
      lineup: "Alignement",
      stats: "Statistiques",
      draft: "Repêchage",
      goalieStats: "Gardiens",
      advancedStats: "Avancées",
      forwards: "Attaquants",
      defense: "Défenseurs",
      goalies: "Gardiens",
      player: "Joueur",
      pos: "POS",
      gp: "MJ",
      goals: "B",
      assists: "A",
      points: "PTS",
      pim: "PUN",
      overall: "TOTAL",
      year: "Année",
      round: "Ronde",
      draftedBy: "Repêché Par",
      wins: "V",
      losses: "D",
      otl: "DP",
      gaa: "MOY",
      svPct: "%ARR",
      shutouts: "BL",
      cf: "CF",
      ca: "CA",
      cfPct: "CF%",
      ff: "FF",
      fa: "FA",
      ffPct: "FF%",
      xGF: "xBP",
      xGA: "xBC",
      xGPct: "xB%",
      toi: "TG",
    },
    footer: {
      tagline: "Votre destination ultime pour les scores, statistiques et couverture en direct du hockey.",
      quickLinks: "Liens Rapides",
      resources: "Ressources",
      company: "Entreprise",
      home: "Accueil",
      schedule: "Calendrier",
      standings: "Classement",
      teams: "Équipes",
      api: "API",
      support: "Support",
      contact: "Contact",
      about: "À Propos",
      careers: "Carrières",
      press: "Presse",
      copyright: "Tous droits réservés.",
    },
    heroExtra: {
      season: "Saison 2025-26",
      alertsLine: "Scores en direct, calendrier & guide TV – Alertes gratuites pour vos équipes",
    },
    homeTeaser: {
      personalization: "Personnalisation",
      title: "Créez Votre Centre Hockey",
      subtitle: "Suivez votre nation favorite, vos équipes et joueurs. Obtenez un tableau de bord personnalisé avec scores en direct, statistiques et alertes de matchs.",
      nationTracking: "Suivi de nation",
      teamAlerts: "Alertes équipe",
      playerStats: "Stats joueurs",
      seeDemo: "Voir la Démo",
      signUpFree: "Inscription Gratuite",
      joinMessage: "Rejoignez des milliers de fans de hockey qui suivent leurs équipes dans le monde entier",
      draftWatch: "Draft 2026",
      draftWatchDesc: "Top espoirs, stats des recrues, vidéos de scouting & classements du repêchage",
    },
    yourTeams: {
      signInTitle: "Connectez-vous pour suivre vos équipes",
      signInDesc: "Créez un compte pour suivre vos ligues et équipes favorites. Recevez des mises à jour personnalisées des matchs et résultats.",
      signIn: "Connexion",
      followTitle: "Suivez Vos Équipes Favorites",
      followDesc: "Sélectionnez vos ligues et équipes favorites pour personnaliser votre expérience IceHub. Accédez rapidement aux résultats, calendriers et alertes.",
      chooseTeams: "Choisir Vos Équipes",
      upgradePro: "Passez à Pro pour personnaliser votre tableau de bord",
      upgradeNow: "Mettre à Niveau",
      title: "Vos Équipes",
      alertsOn: "Alertes Activées",
      alertsOff: "Alertes Désactivées",
      edit: "Modifier",
      upgradeToEdit: "Mettre à niveau pour modifier",
      noGames: "Pas de matchs aujourd'hui pour vos équipes favorites",
      checkBack: "Revenez plus tard ou consultez le calendrier complet",
      viewAll: "Voir tout",
    },
    yourPlayers: {
      signInTitle: "Connectez-vous pour suivre vos joueurs",
      signInDesc: "Créez un compte pour suivre vos joueurs favoris de la LNH, KHL, SHL, Liiga et plus. Recevez des mises à jour sur les stats, buts et matchs à venir.",
      signIn: "Connexion",
      followTitle: "Suivez Vos Joueurs Favoris",
      followDesc: "Suivez des joueurs de la LNH, KHL, SHL, Liiga et plus. Recevez des mises à jour sur les stats, buts et matchs à venir.",
      choosePlayers: "Choisir Vos Joueurs",
      upgradePro: "Passez à Pro pour personnaliser vos joueurs",
      upgradeNow: "Mettre à Niveau",
      title: "Vos Joueurs",
      alertsOn: "Alertes Activées",
      alertsOff: "Alertes Désactivées",
      edit: "Modifier",
      upgradeToEdit: "Mettre à niveau pour modifier",
      viewFullStats: "Voir Toutes les Stats",
      premiumStats: "Stats Premium",
      signInStats: "Connectez-vous pour les stats en direct",
      viewAll: "Voir tout",
    },
    bigEvents: {
      markCalendar: "À Noter Dans Votre Calendrier",
      title: "Prochains Grands Événements",
      subtitle: "Ne manquez aucun match – grands tournois et séries à venir",
      getAlerts: "Recevoir les Alertes – Gratuit !",
      whereToWatch: "Où Regarder",
      scheduleSoon: "Calendrier à venir bientôt",
      moreInfoSoon: "Plus d'infos bientôt",
      nextGame: "Prochain Match",
    },
    navExtra: {
      pricing: "Tarifs",
      myPage: "Ma Page",
      myTeams: "Mes Équipes",
      myPlayers: "Mes Joueurs",
      account: "Compte",
      signOut: "Déconnexion",
      managePlan: "Gérer l'Abonnement",
    },
    sidebar: {
      quickNav: "Navigation Rapide",
      home: "Accueil",
      liveResults: "Live & Résultats",
      leagues: "Ligues & Tournois",
      bettingOdds: "Cotes de Paris",
      statistics: "Statistiques",
      playerStats: "Stats Joueurs",
      playoffBracket: "Arbre des Séries",
      international: "International",
      scheduleWatch: "Calendrier & TV",
      olympics2026: "Olympiques 2026",
      leagueStandings: "Classement",
      hockeyRules: "Règles du Hockey",
      tapToNavigate: "Appuyez pour naviguer • Glissez pour fermer",
    },
    prospects: {
      sectionTitle: "Draft, Recrues & Actualités",
      sectionSubtitle: "Classements du draft LNH 2026, suivi des recrues & actualités hockey en direct",
      lastUpdated: "Dernière mise à jour : 28 février 2026",
      disclaimer: "Les classements des espoirs sont des projections – sujets à changement.",
      tabProspects: "Espoirs",
      tabRookies: "Suivi",
      tabNews: "Actualités",
      draftProspectsTitle: "Espoirs du Draft LNH 2025",
      viewFullRankings: "Classement Complet",
      position: "Position :",
      league: "Ligue :",
      rank: "Rang",
      name: "Nom",
      pos: "Pos",
      team: "Équipe",
      ht: "Taille",
      wt: "Poids",
      scoutingNote: "Note de Scouting",
      media: "Médias",
      noProspects: "Aucun espoir ne correspond aux filtres sélectionnés",
      rookiesTitle: "Recrues 2024-25",
      rookiesSubtitle: "Les meilleurs joueurs de première année dans les ligues",
      noRookies: "Aucune recrue ne correspond au filtre sélectionné",
      rookieTeaser: "Plus de moments forts des recrues bientôt",
      rookieTeaserDesc: "statistiques élargies, suivi du Trophée Calder et analyses vidéo en préparation.",
      liveNews: "Actualités Hockey en Direct",
      newsSource: "Agrégé depuis EliteProspects, NHL.com & DobberProspects",
      scoutingResources: "Ressources de Scouting",
      latestArticles: "Derniers Articles",
      loadingNews: "Chargement des actualités…",
      noNews: "Aucun article disponible pour le moment. Revenez plus tard.",
      neverMiss: "Ne manquez aucune mise à jour importante",
      neverMissDesc: "Les membres Pro & Elite reçoivent des alertes instantanées sur les échanges, les mouvements de draft et les changements de formation.",
      subscribeAlerts: "S'abonner aux Alertes",
    },
  },
};

const languageNames: Record<Language, string> = {
  en: "English",
  sv: "Svenska",
  fi: "Suomi",
  ru: "Русский",
  de: "Deutsch",
  cs: "Čeština",
  fr: "Français",
};

const languageFlags: Record<Language, string> = {
  en: "🇺🇸",
  sv: "🇸🇪",
  fi: "🇫🇮",
  ru: "🇷🇺",
  de: "🇩🇪",
  cs: "🇨🇿",
  fr: "🇫🇷",
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  languageNames: typeof languageNames;
  languageFlags: typeof languageFlags;
  languages: Language[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const validLanguages: Language[] = ["en", "sv", "fi", "ru", "de", "cs", "fr"];

const getInitialLanguage = (): Language => {
  // Check URL params first (for SEO crawlers and shared links)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam && validLanguages.includes(langParam as Language)) {
      return langParam as Language;
    }
  }
  // Fall back to localStorage
  const saved = localStorage.getItem("language");
  if (saved && validLanguages.includes(saved as Language)) {
    return saved as Language;
  }
  // Default to English
  return "en";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
    
    // Update URL with language param for SEO
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('lang', lang);
      window.history.replaceState({}, '', url.toString());
    }
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: handleSetLanguage,
        t: translations[language],
        languageNames,
        languageFlags,
        languages: validLanguages,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
